import { throttle } from 'lodash';
import { useCallback, useContext } from 'react';
import { PlayerAPI, PlayerEvent } from 'bitmovin-player';
import { Button, ButtonConfig, UIInstanceManager } from 'bitmovin-player-ui';
import { screenDetailTypes } from '../../../config/constants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getMixpanelVodProperties } from '../../../contexts/MixpanelHelper';
import playerService from '../../../helpers/bitmovinPlayer';
import f from '../../../helpers/formatter2';
import { __ } from '../../../translations';
import { log } from '@intigral/utils';

/**
 * Converts a timestamp string in the format 'HH:MM:SS:MS' to the total amount of time in seconds.
 *
 * @param stamp - A timestamp string where:
 *   - The first segment is hours (HH)
 *   - The second segment is minutes (MM)
 *   - The third segment is seconds (SS)
 *   - The fourth segment is milliseconds (MS)
 *   Defaults to '00:00:00:00' if not provided.
 * @returns The total amount of time in seconds as a number.
 */
export const convertTimestampToSeconds = (stamp = '00:00:00:00') => {
    return stamp.split(':').reduceRight((acc, time, index) => {
        let totalTime = 0;
        const currTime = parseInt(time, 10);
        switch (index) {
            case 3:
                totalTime = currTime / 1000;
                break;
            case 2:
                totalTime = currTime;
                break;
            case 1:
                totalTime = currTime * 60;
                break;
            case 0:
                totalTime = currTime * 60 * 60;
                break;
        }
        return acc + totalTime;
    }, 0);
};

type GenerateSkipIntroOutButtonFunction = (
    /**
     * @todo add proper type
     */
    onClick: (args: Record<any, any>) => void,
    startTime: string,
    endTime: string,
    title?: string,
    className?: string,
    forceHide?: boolean,
    componentName?: string
) => Button<ButtonConfig>;

/**
 * Adds a visual progress bar to a button. Prograss bar indicates how much of the next episode has been watched.
 * Also adds play icon to the button.
 *
 * @param {Object} button - Bitmovin Button instance to which the progress bar will be added.
 * @param {number} progress - The progress percentage (0-100) to visually represent on the button.
 */
const addProgressBar = (button, progress) => {
    if (progress) {
        const btn = button.getDomElement().elements[0];

        const span = document.createElement('span');
        span.style.position = 'absolute';
        span.style.bottom = '0';
        span.style.left = '0';
        span.style.width = '100%';
        span.style.height = '7%';
        span.style.background = `linear-gradient(to right, #eee, #eee ${progress}%, rgba(0,0,0,0.3) ${progress}%)`;
        btn.append(span);

        const playIcon = document.createElement('span');
        playIcon.style.height = '70%';
        playIcon.style.width = '2rem';
        playIcon.classList.add('play-icon');
        playIcon.style.marginInlineEnd = '-0.5rem';
        playIcon.style.marginInlineStart = '1rem';
        btn.append(playIcon);
    }
};

/**
 * The UI calls `configure()` once the player exists, and removes listeners added through that
 * player when the UI is released. `onConfigure` returns a cleanup that runs on `release()`.
 */
class SkipButton extends Button<ButtonConfig> {
    private readonly onConfigure?: (player: PlayerAPI) => () => void;
    private cleanup?: () => void;

    constructor(config: ButtonConfig, onConfigure?: (player: PlayerAPI) => () => void) {
        super(config);
        this.onConfigure = onConfigure;
    }

    configure(player: PlayerAPI, uimanager: UIInstanceManager): void {
        super.configure(player, uimanager);
        this.cleanup = this.onConfigure?.(player);
    }

    release(): void {
        this.cleanup?.();
        this.cleanup = undefined;
        super.release();
    }
}

type UseSkipButtonsProps = {
    content: any;
    playNextEpisode: (force?: boolean) => void;
    currentEpisode: any;
    seriesPlaylistLength: number;
    isLastEpisodeAcrossSeasons: boolean;
};
const useSkipButtons = ({
    content,
    playNextEpisode,
    currentEpisode,
    seriesPlaylistLength,
    isLastEpisodeAcrossSeasons,
}: UseSkipButtonsProps) => {
    const { track } = useContext(MixpanelContext);

    const generateSkipIntroOutButton: GenerateSkipIntroOutButtonFunction = (
        onClick,
        startTime,
        endTime,
        title = 'Skip Intro',
        className = '',
        forceHide,
        componentName = ''
    ) => {
        const BUTTONS = {
            skipIntro: 'skip-intro',
            skipOutro: 'skip-outro',
            watchCredit: 'watch-credit',
        };

        // TODO: `buttonComponentMap` and `forceHideStatus` are created per button, so each button only knows
        // about itself. Clicking "Skip Outro" can't hide "Watch Credits" (or the other way round), even though
        // both click handlers try to. Share them between the buttons of one UI.
        const buttonComponentMap = {};

        const forceHideStatus = {
            'skip-intro': false,
            'skip-outro': false,
            'watch-credit': false,
        };

        const setForceHideStatusForButton = (button, status) => {
            const isHide = typeof status === 'boolean' ? status : true;
            forceHideStatus[button] = isHide;
            if (isHide) {
                buttonComponentMap[button]?.hide();
            }
        };

        // Shows the button while playback is inside [startTime, endTime].
        const trackPlaybackTime = (player: PlayerAPI) => {
            player.on(PlayerEvent.Seeked, () => {
                setForceHideStatusForButton(componentName, false);
            });

            const onTimeChanged = throttle(() => {
                const adsDuration = (playerService.getAdsData()?.duration ?? 0) / 1000;

                if (forceHideStatus[componentName]) {
                    button.hide();
                    return;
                }

                // A throttled call can still be pending while the player is being destroyed;
                // the player API then throws API_NOT_AVAILABLE.
                let currentTime: number;
                try {
                    currentTime = player.getCurrentTime();
                } catch (e) {
                    log.debug('[useSkipButtons] player API not available:', e);
                    return;
                }

                const playedTime = currentTime - adsDuration;

                if (playedTime > convertTimestampToSeconds(endTime) && componentName === BUTTONS.skipOutro) {
                    playNextEpisode(false);
                    setForceHideStatusForButton(componentName, false);
                    button.hide();
                    return;
                }

                if (
                    playedTime >= convertTimestampToSeconds(startTime) &&
                    playedTime <= convertTimestampToSeconds(endTime)
                ) {
                    button.hide();
                    button.show();
                } else {
                    button.hide();
                }
            }, 500);
            player.on(PlayerEvent.TimeChanged, onTimeChanged);

            return () => onTimeChanged.cancel();
        };

        const shouldTrackPlaybackTime = Boolean(startTime && endTime) && !forceHide;

        const button = new SkipButton(
            { cssClass: `fa custom-info-icon skip-button ${className} bmpui-ui-button` },
            shouldTrackPlaybackTime ? trackPlaybackTime : undefined
        );
        button.setText(title);
        buttonComponentMap[componentName] = button;
        button.onClick.subscribe(function () {
            if (onClick) {
                onClick({ endTime, setForceHideStatusForButton, buttons: buttonComponentMap });
            }
        });
        button.hide();

        // if (componentName === BUTTONS.skipOutro) {
        //     const progress = 75;
        //     addProgressBar(button, progress);
        // }

        return button;
    };

    const generateEventPayload = () => {
        if (!content) return {};

        return {
            ...getMixpanelVodProperties(content),
            'VOD Duration': f.converToHHMMSS(playerService?.player?.getDuration()),
            'Video Type': content.programType === screenDetailTypes.MOVIES ? 'Movie' : 'Episode',
            'Episode Count': seriesPlaylistLength ?? 0,
        };
    };

    const onSkipIntroClick = useCallback(
        throttle(({ endTime }) => {
            if (!playerService.player) return;
            const playerInstance = playerService?.player;
            track('VOD Player - Skip Intro - Click', generateEventPayload());
            playerInstance.seek(
                convertTimestampToSeconds(endTime) + (playerService.getAdsData()?.duration ?? 0) / 1000
            );
        }, 2500),
        [content]
    );

    const onSkipOutroClick = useCallback(
        throttle(({ endTime, setForceHideStatusForButton }) => {
            if (!playerService.player) return;
            if (content && content.programType === screenDetailTypes.MOVIES) return;
            if (seriesPlaylistLength === 0) {
                return;
            }

            setForceHideStatusForButton('skip-outro');
            setForceHideStatusForButton('watch-credit');

            track('VOD Player - Skip Outro - Click', generateEventPayload());

            playNextEpisode(true);
        }, 2500),
        [content, seriesPlaylistLength]
    );

    const onWatchCredit = throttle(({ setForceHideStatusForButton }) => {
        setForceHideStatusForButton('skip-outro');
        setForceHideStatusForButton('watch-credit');
    }, 2500);

    if (!content) {
        return { renderSkipIntro: null, renderSkipOutro: null, renderWatchCredit: null };
    }

    const renderSkipIntro = () => {
        const asset = content.programType === screenDetailTypes.MOVIES ? content : currentEpisode;

        if (!asset) return;

        const { introSt, introEt } = asset;
        const skipIntroButton = generateSkipIntroOutButton(
            onSkipIntroClick,
            introSt,
            introEt,
            __('player_skipintro_intro_label'),
            'skip-intro',
            false,
            'skip-intro'
        );
        return skipIntroButton;
    };

    const renderSkipOutro = () => {
        const asset = content.programType === screenDetailTypes.MOVIES ? content : currentEpisode;
        const forceHideOutro = content.programType === screenDetailTypes.MOVIES || isLastEpisodeAcrossSeasons;

        const { outroSt, outroEt } = asset;

        const skipOutroButton = generateSkipIntroOutButton(
            onSkipOutroClick,
            outroSt,
            outroEt,
            __('player_skipintro_outro_label'),
            'skip-outro',
            forceHideOutro,
            'skip-outro'
        );
        return skipOutroButton;
    };

    const renderWatchCredit = () => {
        const asset = content.programType === screenDetailTypes.MOVIES ? content : currentEpisode;
        const forceHideOutro = content.programType === screenDetailTypes.MOVIES ? true : false;
        const { outroSt, outroEt } = asset;

        const watchCreditButton = generateSkipIntroOutButton(
            onWatchCredit,
            outroSt,
            outroEt,
            __('player_skipintro_watchcredit_label'),
            'watch-credit',
            forceHideOutro,
            'watch-credit'
        );
        return watchCreditButton;
    };

    return { renderSkipIntro, renderSkipOutro, renderWatchCredit };
};

export default useSkipButtons;
